/**
 * Ortsprognose in der Meteo-Spalte.
 *
 * Oben die Ortswahl, darunter sieben Tage, darunter der Stundenverlauf des
 * gewählten Tags. Die Daten kommen aus src/data/meteo.js.
 *
 * Der Verlauf ist gezeichnet und nicht aufgelistet: vierundzwanzig Zeilen
 * Zahlen liest niemand, eine Kurve mit Regenbalken schon - und in einer Spalte
 * von 320 Punkten ist für beides zusammen kein Platz.
 */
import { ARTEN, naechster, prognose, suche } from '../data/meteo.js';

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const pad = (n) => String(n).padStart(2, '0');
const komma = (x, stellen = 1) =>
  x == null ? '–' : x.toFixed(stellen).replace('.', ',');

/** Breite und Höhe des gezeichneten Verlaufs, in eigenen Einheiten. */
const W = 320;
const H = 96;
const RAND = { oben: 10, unten: 20, links: 25, rechts: 6 };

function svgEl(name, attrs = {}) {
  const el = document.createElementNS('http://www.w3.org/2000/svg', name);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

/**
 * Das Wettersymbol - vier Stufen Bewölkung, bei Niederschlag mit Tropfen.
 *
 * Bewusst gezeichnet statt als Zeichen aus der Schrift: Wettersymbole in
 * Unicode sehen auf jedem System anders aus, und die Hausschrift hat keine.
 */
function symbol(stufe, nass, nacht) {
  const s = svgEl('svg', { viewBox: '0 0 20 20', class: 'wxsym', 'aria-hidden': 'true' });
  // Regen faellt aus einer Wolke. Das Tagessymbol kommt aus dem Mittag, die
  // Regenmenge aus dem ganzen Tag - sonst stuenden Tropfen unter blankem
  // Himmel, wenn es erst am Abend schuettet.
  if (nass) stufe = Math.max(stufe, 1);
  if (stufe === 0 || stufe === 1) {
    // Klar: Sonne, nachts Mond.
    if (nacht) {
      s.append(svgEl('path', { d: 'M13 4a6 6 0 1 0 4 8 5 5 0 0 1-4-8z', class: 'wxsym__hell' }));
    } else {
      s.append(svgEl('circle', { cx: stufe === 1 ? 8 : 10, cy: stufe === 1 ? 7 : 10, r: 4, class: 'wxsym__hell' }));
    }
  }
  if (stufe >= 1) {
    s.append(svgEl('path', {
      d: 'M6 15h8a3 3 0 0 0 .3-6A4.5 4.5 0 0 0 5.6 10 2.6 2.6 0 0 0 6 15z',
      class: stufe === 3 ? 'wxsym__voll' : 'wxsym__wolke'
    }));
  }
  if (nass) {
    for (const x of [8, 12]) {
      s.append(svgEl('line', { x1: x, y1: 16, x2: x - 1, y2: 19, class: 'wxsym__regen' }));
    }
  }
  return s;
}

export function createMeteoPanel({ map, onOrt }) {
  const n = {};
  let ort = null;          // gewählter Punkt aus dem Verzeichnis
  let daten = null;        // Prognose dazu
  let tag = 0;             // gewählter Tag
  let suchTimer = null;

  function kartenmitte() {
    const c = map.getCenter();
    return { lat: c.lat, lng: c.lng };
  }

  async function ortSetzen(p) {
    if (!p) return;
    ort = p;
    n.name.textContent = p.name.trim();
    n.lage.textContent = [
      p.plz || '',
      `${p.hoehe} m`,
      ARTEN[p.typ] || '',
      Number.isFinite(p.km) && p.km >= 0.1 ? `${komma(p.km)} km entfernt` : ''
    ].filter(Boolean).join(' · ');
    n.kopf.hidden = false;
    n.liste.textContent = '';
    n.fehler.hidden = true;
    n.tage.textContent = '';
    n.verlauf.textContent = '';
    n.stand.textContent = 'wird geholt …';
    const dieser = p;
    try {
      const neu = await prognose(p.id);
      // Inzwischen ein anderer Ort gewählt: die spätere Antwort darf nicht siegen.
      if (ort !== dieser) return;
      daten = neu;
      tag = 0;
      zeichnen();
      onOrt?.(p);
    } catch (e) {
      if (ort !== dieser) return;
      daten = null;
      n.stand.textContent = '';
      n.fehler.textContent = `Prognose nicht erreichbar (${e.message})`;
      n.fehler.hidden = false;
    }
  }

  /** Die sieben Tage. Der angebrochene erste sagt, ab wann er gilt. */
  function tageZeichnen() {
    n.tage.textContent = '';
    daten.tage.forEach((t, i) => {
      const d = new Date(`${t.tag}T12:00:00`);
      const knopf = document.createElement('button');
      knopf.type = 'button';
      knopf.className = 'wxtag' + (i === tag ? ' wxtag--an' : '');
      knopf.setAttribute('aria-pressed', String(i === tag));

      const name = document.createElement('span');
      name.className = 'wxtag__tag';
      name.textContent = i === 0 && t.von > 0
        ? `heute ab ${t.von} Uhr`
        : `${WOCHENTAG[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.`;

      const bild = document.createElement('span');
      bild.className = 'wxtag__bild';
      if (t.b != null) bild.append(symbol(t.b, (t.regen || 0) >= 0.2, false));

      const grad = document.createElement('span');
      grad.className = 'wxtag__grad';
      grad.innerHTML = t.max == null ? '–'
        : `${t.max}°<span class="wxtag__min">${t.min}°</span>`;

      const regen = document.createElement('span');
      regen.className = 'wxtag__regen';
      regen.textContent = (t.regen || 0) >= 0.2 ? `${komma(t.regen)} mm` : '';

      knopf.append(name, bild, grad, regen);
      knopf.addEventListener('click', () => { tag = i; zeichnen(); });
      n.tage.append(knopf);
    });
  }

  /**
   * Der Stundenverlauf: Temperaturkurve, Regenbalken, Nacht hinterlegt.
   *
   * Die Temperaturachse spannt immer mindestens sechs Grad, sonst macht die
   * Kurve aus einem halben Grad Unterschied ein Gebirge.
   */
  function verlaufZeichnen() {
    n.verlauf.textContent = '';
    const stunden = daten.stunden.filter((s) => s.d === tag);
    if (stunden.length < 2) {
      n.verlauf.append(Object.assign(document.createElement('p'),
        { className: 'hint', textContent: 'Für diesen Tag liegen keine Stundenwerte vor.' }));
      return;
    }

    const temps = stunden.map((s) => s.t).filter((x) => x != null);
    let tMin = Math.min(...temps);
    let tMax = Math.max(...temps);
    const mitte = (tMin + tMax) / 2;
    if (tMax - tMin < 6) { tMin = mitte - 3; tMax = mitte + 3; }
    const regenMax = Math.max(1, ...stunden.map((s) => s.r || 0));

    const innenB = W - RAND.links - RAND.rechts;
    const innenH = H - RAND.oben - RAND.unten;
    const x = (i) => RAND.links + (innenB * i) / Math.max(1, stunden.length - 1);
    const y = (t) => RAND.oben + innenH * (1 - (t - tMin) / (tMax - tMin));

    const s = svgEl('svg', {
      viewBox: `0 0 ${W} ${H}`, class: 'wxkurve',
      role: 'img',
      'aria-label': `Stundenverlauf ${daten.tage[tag].tag}: ${tMin.toFixed(0)} bis ${tMax.toFixed(0)} Grad`
    });

    // Nacht hinterlegen - ein Feld je Stunde, damit keine Annahme über
    // Sonnenauf- und -untergang nötig ist.
    const schritt = innenB / Math.max(1, stunden.length - 1);
    // Zusammenhaengende Nachtstrecken als *ein* Feld, nicht als ein Rechteck
    // je Stunde: nebeneinanderliegende Flaechen hinterlassen im Browser helle
    // Nahtstellen, und dann sieht die Nacht aus wie ein Balkendiagramm.
    for (let i = 0; i < stunden.length;) {
      if (!stunden[i].n) { i += 1; continue; }
      let j = i;
      while (j + 1 < stunden.length && stunden[j + 1].n) j += 1;
      // Am Rand beschneiden: die erste und die letzte Stunde liegen auf der
      // Achse, ihr halbes Feld ragte sonst aus der Zeichenflaeche.
      const von = Math.max(RAND.links, x(i) - schritt / 2);
      const bis = Math.min(W - RAND.rechts, x(j) + schritt / 2);
      if (bis > von) {
        s.append(svgEl('rect', {
          x: von, y: RAND.oben, width: bis - von, height: innenH, class: 'wxkurve__nacht'
        }));
      }
      i = j + 1;
    }

    // Regen als Balken von unten.
    stunden.forEach((h, i) => {
      if (!h.r) return;
      const hoehe = (innenH * Math.min(1, h.r / regenMax)) * 0.55;
      s.append(svgEl('rect', {
        x: x(i) - schritt * 0.35, y: RAND.oben + innenH - hoehe,
        width: Math.max(1.5, schritt * 0.7), height: hoehe, class: 'wxkurve__regen'
      }));
    });

    // Temperaturkurve.
    const punkte = stunden.map((h, i) => (h.t == null ? null : `${x(i)},${y(h.t)}`))
      .filter(Boolean).join(' ');
    s.append(svgEl('polyline', { points: punkte, class: 'wxkurve__temp' }));

    // Achsen: zwei Gradzahlen links, Stunden unten alle drei Stunden.
    for (const t of [tMax, tMin]) {
      const b = svgEl('text', { x: RAND.links - 5, y: y(t) + 3, class: 'wxkurve__achse', 'text-anchor': 'end' });
      b.textContent = `${Math.round(t)}°`;
      s.append(b);
    }
    // Beim angebrochenen ersten Tag bleiben nur wenige Stunden uebrig - dort
    // stuende sonst eine einzige Zahl unter der Kurve.
    const schrittweite = stunden.length <= 9 ? 1 : 3;
    stunden.forEach((h, i) => {
      const std = h.zeit.getHours();
      if (std % schrittweite !== 0) return;
      const b = svgEl('text', { x: x(i), y: H - 6, class: 'wxkurve__achse', 'text-anchor': 'middle' });
      b.textContent = pad(std);
      s.append(b);
    });

    // Der Strich für jetzt, wenn der gewählte Tag heute ist.
    const jetzt = Date.now();
    const erster = stunden[0].zeit.getTime();
    const letzter = stunden[stunden.length - 1].zeit.getTime();
    if (jetzt >= erster - 1800000 && jetzt <= letzter + 1800000) {
      const anteil = (jetzt - erster) / Math.max(1, letzter - erster);
      const px = RAND.links + innenB * Math.max(0, Math.min(1, anteil));
      s.append(svgEl('line', {
        x1: px, y1: RAND.oben - 3, x2: px, y2: RAND.oben + innenH, class: 'wxkurve__jetzt'
      }));
    }

    n.verlauf.append(s);

    // Darunter in Worten, was die Kurve nicht sagt: Regenmenge und Wind.
    const summe = stunden.reduce((a, h) => a + (h.r || 0), 0);
    const wind = Math.max(...stunden.map((h) => h.w || 0));
    const zeile = document.createElement('p');
    zeile.className = 'hint hint--block';
    zeile.textContent = [
      summe >= 0.2 ? `${komma(summe)} mm Niederschlag` : 'kein Niederschlag',
      wind ? `Wind bis ${wind} km/h` : ''
    ].filter(Boolean).join(' · ');
    n.verlauf.append(zeile);
  }

  function zeichnen() {
    if (!daten) return;
    tageZeichnen();
    verlaufZeichnen();
    const l = daten.lauf;
    const t = l && l.length === 12
      ? new Date(Date.UTC(+l.slice(0, 4), +l.slice(4, 6) - 1, +l.slice(6, 8), +l.slice(8, 10)))
      : null;
    n.stand.textContent = t ? `Lauf von ${pad(t.getHours())}:${pad(t.getMinutes())} Uhr` : '';
  }

  /** Vorschläge zum Suchtext. */
  async function suchen() {
    const text = n.feld.value;
    if (text.trim().length < 2) { n.liste.textContent = ''; return; }
    const mitte = kartenmitte();
    let treffer = [];
    try {
      treffer = await suche(text, { lat: mitte.lat, lng: mitte.lng });
    } catch { /* ohne Verzeichnis gibt es keine Vorschläge */ }
    n.liste.textContent = '';
    for (const p of treffer) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'wxort';
      const wer = document.createElement('span');
      wer.textContent = p.name.trim();
      const rand = document.createElement('span');
      rand.className = 'wxort__rand';
      rand.textContent = p.plz || `${p.hoehe} m`;
      b.append(wer, rand);
      b.addEventListener('click', () => { n.feld.value = ''; n.liste.textContent = ''; ortSetzen(p); });
      n.liste.append(b);
    }
  }

  async function ausKarte() {
    const m = kartenmitte();
    try {
      await ortSetzen(await naechster(m.lat, m.lng));
    } catch (e) {
      n.fehler.textContent = `Verzeichnis nicht erreichbar (${e.message})`;
      n.fehler.hidden = false;
    }
  }

  function ausStandort() {
    if (!navigator.geolocation) return;
    n.stand.textContent = 'Standort wird gesucht …';
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await ortSetzen(await naechster(pos.coords.latitude, pos.coords.longitude));
        } catch { n.stand.textContent = 'Verzeichnis nicht erreichbar'; }
      },
      () => { n.stand.textContent = 'Standort nicht verfügbar'; },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 120000 }
    );
  }

  function mount(host) {
    if (!host) return;
    host.textContent = '';
    host.classList.add('wx');

    const kopfzeile = document.createElement('div');
    kopfzeile.className = 'view__head';
    kopfzeile.innerHTML = '<h3>Ortsprognose</h3>';
    const mitteKnopf = document.createElement('button');
    mitteKnopf.type = 'button';
    mitteKnopf.className = 'linkbtn';
    mitteKnopf.textContent = 'Kartenmitte';
    kopfzeile.append(mitteKnopf);

    // Dasselbe Feld mit Standortknopf wie bei der Route - ◎ heisst in dieser
    // Anwendung überall dasselbe.
    const suchzeile = document.createElement('span');
    suchzeile.className = 'field__with';
    const feld = document.createElement('input');
    feld.type = 'search';
    feld.placeholder = 'Ort oder Postleitzahl';
    feld.autocomplete = 'off';
    feld.setAttribute('aria-label', 'Ort für die Prognose');
    const gps = document.createElement('button');
    gps.type = 'button';
    gps.className = 'field__here';
    gps.textContent = '◎';
    gps.title = 'Eigenen Standort verwenden';
    gps.setAttribute('aria-label', 'Eigenen Standort verwenden');
    suchzeile.append(feld, gps);

    const liste = document.createElement('div');
    liste.className = 'wx__liste';

    const kopf = document.createElement('div');
    kopf.className = 'wx__ort';
    kopf.hidden = true;
    const name = document.createElement('strong');
    const lage = document.createElement('span');
    lage.className = 'wx__lage';
    kopf.append(name, lage);

    const tage = document.createElement('div');
    tage.className = 'wx__tage';
    const verlauf = document.createElement('div');
    verlauf.className = 'wx__verlauf';
    const stand = document.createElement('p');
    stand.className = 'hint';
    const fehler = document.createElement('p');
    fehler.className = 'hint hint--block';
    fehler.hidden = true;

    host.append(kopfzeile, suchzeile, liste, kopf, tage, verlauf, stand, fehler);
    Object.assign(n, { feld, liste, kopf, name, lage, tage, verlauf, stand, fehler });

    feld.addEventListener('input', () => {
      clearTimeout(suchTimer);
      suchTimer = setTimeout(suchen, 160);
    });
    gps.addEventListener('click', ausStandort);
    mitteKnopf.addEventListener('click', ausKarte);
  }

  return {
    mount,
    /** Beim ersten Öffnen der Spalte den nächstgelegenen Ort nehmen. */
    async zeigen() { if (!ort) await ausKarte(); },
    setzen: ortSetzen
  };
}
